import {useEffect, useRef} from 'react';
import {ViewerAction} from './useViewerActions';

interface ViewerShortcutsInput {
    actions: ViewerAction[];
    /** A dialog or menu is open and handles its own keys (e.g. Escape closes it, not the viewer). */
    suspended: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onClose: () => void;
    /** Zooms by a factor about the stage's centre. */
    onZoom: (factor: number) => void;
    onResetZoom: () => void;
}

const KEY_ZOOM_FACTOR = 1.5;

// Inputs that take no keys of their own: e.g. the "Eredeti" switch's, focused once it is clicked.
const KEYLESS_INPUT_TYPES = ['checkbox', 'button', 'submit', 'reset'];

/** True when a key event comes from a text field, where viewer shortcuts must not fire. */
function isTypingTarget(target: EventTarget | null): boolean {
    if (target instanceof HTMLInputElement) return !KEYLESS_INPUT_TYPES.includes(target.type);
    return target instanceof HTMLElement
        && (target.isContentEditable || ['TEXTAREA', 'SELECT'].includes(target.tagName));
}

/**
 * The viewer's keyboard: arrows step, Esc or G closes, + - 0 zoom, and every action runs by its own
 * `keys`. Keep in sync with AboutModal's list.
 */
export function useViewerShortcuts(input: ViewerShortcutsInput) {
    // Re-assigned every render so the single listener below always sees current state
    // (e.g. whether the current photo is starred right after toggling it).
    const onKeyDownRef = useRef<(e: KeyboardEvent) => void>(() => {});
    onKeyDownRef.current = (e: KeyboardEvent) => {
        if (isTypingTarget(e.target) || input.suspended) return;
        const plain = !e.ctrlKey && !e.metaKey && !e.altKey;
        const key = e.key.toLowerCase();
        if (e.key === 'ArrowLeft') input.onPrevious();
        else if (e.key === 'ArrowRight') input.onNext();
        else if (key === 'g' && plain) input.onClose();     // Closing on 'Escape' is handled by Chakra dialog
        else if (e.key === '+' && plain) input.onZoom(KEY_ZOOM_FACTOR);
        else if (e.key === '-' && plain) input.onZoom(1 / KEY_ZOOM_FACTOR);
        else if (e.key === '0' && plain) input.onResetZoom();
        else if (plain) {
            const action = input.actions.find((a) => a.keys?.includes(key));
            if (!action) return;
            // Backspace would otherwise navigate back in some browsers.
            e.preventDefault();
            action.run();
        }
    };

    useEffect(() => {
        const listener = (e: KeyboardEvent) => onKeyDownRef.current(e);
        window.addEventListener('keydown', listener);
        return () => window.removeEventListener('keydown', listener);
    }, []);
}
